/**
 * AC-1479: `useQueuePayloadFields(queueId, sample?)` - TanStack Query read
 * hook for the payload-field discovery endpoint of a single queue.
 *
 * Fetches via `apiService.getQueuePayloadFields(queueId, sample)` (AC-1477
 * endpoint). The response shape is contract-bound to the actual server
 * response: `{ queue_id, total_items, sample_size, skipped_oversize,
 * fields[] }`.
 *
 * ## Key shape
 *
 * `queryKeys.queuePayloadFields(queueId)` produces
 * `['queue', Number(queueId), 'payload-fields']`. Hierarchical under
 * `queryKeys.queue(queueId)` so a single
 * `invalidateQueries(queryKeys.queue(id))` cascades to the discovery cache.
 *
 * ## Defaults
 *
 * - `sample` defaults to 100, matching the backend's default.
 *
 * ## Enabled gating
 *
 * Treats undefined / null / non-positive queue IDs as "no queue selected
 * yet" so stray call sites do not fire `GET /queues/0/payload-fields`.
 * Mirrors the `useQueuePriorityHistogram` precedent.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { PayloadFieldsResponse } from '../../types';
import { queryKeys } from './queryKeys';

export function useQueuePayloadFields(
  queueId: number,
  sample: number = 100,
): UseQueryResult<PayloadFieldsResponse, Error> {
  const hasId =
    queueId !== undefined && queueId !== null && Number(queueId) > 0;

  return useQuery<PayloadFieldsResponse, Error>({
    queryKey: queryKeys.queuePayloadFields(queueId),
    queryFn: () => apiService.getQueuePayloadFields(Number(queueId), sample),
    enabled: hasId,
  });
}

export default useQueuePayloadFields;
